<div class="npdc-title">
  <div id="titleContainer">
    @if (mapEntry().content.preamble) {
      <h4>
        {{ mapEntry().content.preamble }}
      </h4>
    }
    <h1>
      {{ mapEntry().content.title }}
    </h1>
    @if (mapEntry().content.subtitle) {
      <h4>
        {{ mapEntry().content.subtitle }}
      </h4>
    }
  </div>

  <div class="npdc-title__actions">
    <button class="npdc-toolbar-button" mat-button (click)="onClose()">Close</button>
  </div>
</div>

<div class="npdc-layout__content">
  <mat-stepper orientation="vertical" [linear]="isLinear()" #stepper>
    <!-- Map Upload -->

    <mat-step>
      <ng-template matStepLabel>Map scans</ng-template>
      <input
        #fileInput
        type="file"
        title="blob"
        name="blob"
        placeholder="Select one or more files to upload"
        multiple
        hidden
        (change)="sourceUpload($event)"
      />
      <p>
        @if (!sourceImages.selectedOptions.selected.length) {
          <button mat-flat-button (click)="fileInput.click()">Upload map</button>
        } @else {
          <button
            mat-flat-button
            (click)="attachmentEdit(sourceList, sourceImages.selectedOptions.selected)"
          >
            Edit
          </button>
          @if (
            authService.user() &&
            (permissions()?.mayDelete ||
              authService.canInvoke(authService.endpoints.deleteMapArchiveEntry))
          ) {
            <button
              mat-button
              (click)="attachmentDelete(sourceList, sourceImages.selectedOptions.selected)"
            >
              Delete
            </button>
          }
        }
      </p>
      <mat-selection-list #sourceImages>
        @for (image of sourceList.value()?.items; track image.id) {
          <mat-list-option togglePosition="before" [value]="image.id">
            <span matListItemTitle>{{ image.title || image.filename }}</span>
            <span matListItemLine>
              <strong>Prefix:</strong>
              {{ image.prefix }}
            </span>
            <span matListItemLine>
              @if (image.released) {
                <strong>Released:</strong>
                {{ image.released | date }}
              }
              <strong>Size:</strong>
              {{ image.byteSize | humanReadableSize }}
            </span>
          </mat-list-option>
        }
      </mat-selection-list>
      <p>
        <mat-paginator
          showFirstLastButtons
          (page)="sourcePageChange($event)"
          [length]="sourceList.value()?.count"
          [pageIndex]="sourcePage()"
          [pageSize]="sourcePageSize()"
          [pageSizeOptions]="[defaultPageSize, 25, 50, 100]"
        />
      </p>
    </mat-step>

    <!-- Map Publication Metadata -->

    @for (img of sourceList.value()?.items; track img.id) {
      <mat-step>
        <ng-template matStepLabel>Publication info - {{ img.title || img.filename }}</ng-template>
        <form id="publication-form" [formGroup]="scanDetails[img.id]" (ngSubmit)="submit()">
          <p>
            <mat-form-field appearance="outline">
              <mat-label>Year</mat-label>
              <input type="number" matInput [formControl]="scanDetailsYear[img.id]" />
            </mat-form-field>
          </p>

          <p>
            <app-form-control-autocomplete
              label="Country"
              (searchString)="countrySearch.set($event)"
              [resource]="countryResource"
              [formControl]="scanDetailsCountry[img.id]"
            />
          </p>

          <p>
            <mat-form-field appearance="outline">
              <mat-label>Place</mat-label>
              <input matInput [formControl]="scanDetailsPlace[img.id]" />
            </mat-form-field>
          </p>

          <p>
            <app-form-control-autocomplete
              label="Languages"
              [multiple]="true"
              (searchString)="langSearch.set($event)"
              [resource]="langResource"
              [formControl]="scanDetailsLanguages[img.id]"
            />
          </p>

          <p>
            <mat-form-field appearance="outline">
              <mat-label>Code</mat-label>
              <input type="text" matInput [formControl]="scanDetailsCode[img.id]" />
            </mat-form-field>
          </p>

          <p>
            <mat-form-field appearance="outline">
              <mat-label>License</mat-label>
              <mat-select [formControl]="scanDetailsLicense[img.id]">
                @for (license of mapLicense | keyvalue; track license.value) {
                  <mat-option value="{{ license.value }}">{{ license.value }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </p>

          <button mat-flat-button type="submit">Save</button>
        </form>
      </mat-step>
    }

    <!-- General Metadata -->

    <mat-step>
      <form id="general-form" [formGroup]="generalForm" (ngSubmit)="submit()">
        <ng-template matStepLabel>Description</ng-template>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Preamble</mat-label>
            <input type="text" matInput [formControl]="preamble" />
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Title</mat-label>
            <input type="text" matInput [formControl]="title" />
          </mat-form-field>
        </p>
        <p>
          <mat-form-field appearance="outline">
            <mat-label>Subtitle</mat-label>
            <input type="text" matInput [formControl]="subtitle" />
          </mat-form-field>
        </p>

        <p>
          <mat-form-field appearance="outline">
            <mat-label>Collection type</mat-label>
            <mat-select [formControl]="entryType">
              @if (sourceCount() === 1) {
                <mat-option value="single">Single</mat-option>
              } @else {
                @for (type of publicationType | keyvalue; track type.key) {
                  <mat-option value="{{ type.value }}" [disabled]="type.value === 'single'">
                    {{ type.key }}
                  </mat-option>
                }
              }
            </mat-select>
            <mat-hint>
              <strong>- Single:</strong> Contains only one scanned image.<br />
              <strong>- Set:</strong> Single map divided in multiple scans (front and back,
              quadrants, etc..)<br />
              <strong>- Series:</strong> Multiple scans showing a map that is updated over time.<br />
            </mat-hint>
          </mat-form-field>
        </p>

        <p>
          <mat-form-field appearance="outline">
            <mat-label>Map series</mat-label>
            <input type="text" matInput [formControl]="serie" />
          </mat-form-field>
        </p>

        <p>
          <app-form-control-markdown label="Summary" [formControl]="summary" />
        </p>

        <p>
          <mat-form-field appearance="outline">
            <mat-label>Scale</mat-label>
            <input type="text" matInput [formControl]="scales" />
            <mat-hint> For multi-scale provide a comma separated list: 10000, 2000, ... </mat-hint>
          </mat-form-field>
        </p>

        <p>
          <mat-form-field appearance="outline">
            <mat-label>Map type</mat-label>
            <mat-select [formControl]="mapType">
              @for (mt of mapTypes | keyvalue; track mt.key) {
                <mat-option value="{{ mt.key }}">{{ mt.value }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </p>

        <button mat-flat-button type="submit">Save</button>
      </form>
    </mat-step>

    <!-- Geographical Metadata -->

    <mat-step>
      <ng-template matStepLabel>Geographical extent</ng-template>
      <p>
        <mat-card class="npdc-leaflet" appearance="outlined">
          <app-leaflet-group
            [geometry]="mapEntry().content.geojson"
            [readonly]="false"
            [height]="'500px'"
            (geometryChange)="onGeoChange($event)"
          />
        </mat-card>
      </p>
    </mat-step>

    <!-- Contributor Metadata -->

    <mat-step>
      <ng-template matStepLabel>Contributors</ng-template>
      <p>
        @if (!contribList.selectedOptions.selected.length) {
          <button mat-flat-button (click)="contributorForm()">Add</button>
        } @else {
          @if (contribList.selectedOptions.selected.length === 1) {
            <button
              mat-flat-button
              (click)="contributorForm(contribList.selectedOptions.selected[0].value)"
            >
              Edit
            </button>
            <button mat-button (click)="contributorDelete(contribList.selectedOptions.selected)">
              Delete
            </button>
          } @else {
            <button
              mat-flat-button
              (click)="contributorDelete(contribList.selectedOptions.selected)"
            >
              Delete
            </button>
          }
        }
      </p>
      <mat-selection-list #contribList>
        @for (contrib of mapEntry().content.contributors; track contrib.name) {
          <mat-list-option togglePosition="before" [value]="contrib">
            <span matListItemTitle>{{ contrib.name }}</span>
            @if (contrib.email) {
              <span matListItemLine>
                <strong>Email:</strong>
                {{ contrib.email }}
              </span>
            }
            @if (contrib.roles) {
              <span matListItemLine>
                <strong>Roles:</strong>
                {{ contrib.roles.join(', ') }}
              </span>
            }
          </mat-list-option>
        }
      </mat-selection-list>
    </mat-step>

    <!-- Organisation Metadata -->

    <mat-step>
      <ng-template matStepLabel>Organisations</ng-template>
      <p>
        @if (!orgList.selectedOptions.selected.length) {
          <button mat-flat-button (click)="orgForm()">Add</button>
        } @else {
          @if (orgList.selectedOptions.selected.length === 1) {
            <button mat-flat-button (click)="orgForm(orgList.selectedOptions.selected[0].value)">
              Edit
            </button>
          }
          <button mat-button (click)="orgDelete(orgList.selectedOptions.selected)">Delete</button>
        }
      </p>
      <mat-selection-list #orgList>
        @for (org of mapEntry().content.organisations; track org.name) {
          <mat-list-option togglePosition="before" [value]="org">
            <span matListItemTitle>{{ org.name }}</span>
            @if (org.url) {
              <span matListItemLine>
                <strong>URL:</strong>
                {{ org.url }}
              </span>
            }
            @if (org.roles) {
              <span matListItemLine>
                <strong>Roles:</strong>
                {{ org.roles.join(', ') }}
              </span>
            }
          </mat-list-option>
        }
      </mat-selection-list>
    </mat-step>
  </mat-stepper>
</div>
